import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import Swal from 'sweetalert2';

export default function HistoryIndex() {
    const { t } = useContext(TranslatorContext);

    const [parentHistories, setParentHistories] = useState([]);
    const [childHistories, setChildHistories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedParent, setSelectedParent] = useState(null);
    const [currentId, setCurrentId] = useState(null);
    const [currentType, setCurrentType] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Store Modal States
    const [storeModal, setStoreModal] = useState(false);
    const [storeForm, setStoreForm] = useState({ name: '', parent_id: '' });
    const [storeErrors, setStoreErrors] = useState({});
    const [storeLoading, setStoreLoading] = useState(false);

    // Update Modal States
    const [updateModal, setUpdateModal] = useState(false);
    const [updateForm, setUpdateForm] = useState({ id: null, name: '', parent_id: '' });
    const [updateErrors, setUpdateErrors] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);

    useEffect(() => {
        fetchHistories();
    }, []);

    useEffect(() => {
        if (selectedParent) {
            loadChildHistories(selectedParent.id);
        }
    }, [selectedParent]);

    const fetchHistories = () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        axios.get(`${process.env.REACT_APP_API_URL}/history`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                const allHistories = res.data;
                const parents = allHistories.filter(item => !item.parent_id);
                setParentHistories(parents);

                if (parents.length > 0 && !selectedParent) {
                    setSelectedParent(parents[0]);
                } else if (selectedParent) {
                    const updatedParent = parents.find(p => p.id === selectedParent.id);
                    if (updatedParent) {
                        setSelectedParent(updatedParent);
                    }
                }
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    const loadChildHistories = (parentId) => {
        const token = localStorage.getItem("authToken");
        axios.get(`${process.env.REACT_APP_API_URL}/history`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
            const children = res.data.filter(item => item.parent_id === parentId);
            setChildHistories(children);
        }).catch(err => console.error(err));
    };

    // Store Modal Functions
    const openStoreModal = (type, parentId = null) => {
        if (type === 'child' && !parentId) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('Please select a category first'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
            return;
        }
        setStoreForm({
            name: '',
            parent_id: type === 'child' ? parentId : ''
        });
        setStoreErrors({});
        setStoreModal(true);
    };

    const handleStoreChange = (e) => {
        const { name, value } = e.target;
        setStoreForm(prev => ({ ...prev, [name]: value }));
        if (storeErrors[name]) {
            setStoreErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleStoreSubmit = async (e) => {
        e.preventDefault();
        setStoreLoading(true);
        setStoreErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/history`,
                storeForm,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.history) {
                fetchHistories();
                setStoreModal(false);
                setStoreForm({ name: '', parent_id: '' });

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: storeForm.parent_id ? t('Value created successfully') : t('History created successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setStoreErrors(error.response.data.errors || {});

                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error creating history:', error);

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to create'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setStoreLoading(false);
        }
    };

    // Update Modal Functions
    const openUpdateModal = (item, type) => {
        setCurrentType(type);
        setUpdateForm({
            id: item.id,
            name: item.name,
            parent_id: item.parent_id || ''
        });
        setUpdateErrors({});
        setUpdateModal(true);
    };

    const handleUpdateChange = (e) => {
        const { name, value } = e.target;
        setUpdateForm(prev => ({ ...prev, [name]: value }));
        if (updateErrors[name]) {
            setUpdateErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        setUpdateErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/history-update/${updateForm.id}`,
                {
                    name: updateForm.name,
                    parent_id: updateForm.parent_id || null
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.history) {
                fetchHistories();
                setUpdateModal(false);

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: currentType === 'parent' ? t('History updated successfully') : t('Value updated successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setUpdateErrors(error.response.data.errors || {});

                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else if (error.response?.status === 404) {
                setUpdateErrors({ general: [error.response.data.error] });

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response.data.error || t('Record not found'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error updating history:', error);

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to update'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    // Delete Function - FIXED (accepts id and type as parameters)
    const handleDelete = async (id, type) => {
        setDeleteLoading(true);
        const token = localStorage.getItem("authToken");

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/history-delete/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchHistories();

            Swal.fire({
                icon: 'success',
                title: t('deleted'),
                text: type === 'parent'
                    ? t('History category deleted successfully')
                    : t('Value deleted successfully'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } catch (error) {
            console.error('Error deleting history:', error);

            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('Failed to delete'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } finally {
            setDeleteLoading(false);
            setCurrentId(null);
            setCurrentType(null);
        }
    };

    // Delete confirmation with SweetAlert - FIXED
    const confirmDelete = (id, type) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: type === 'parent'
                ? t("Want to delete this history category?")
                : t("Want to delete this value?"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('delete'),
            cancelButtonText: t('cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                handleDelete(id, type); // Pass parameters directly
            }
        });
    };

    if (loading) return <PageLayout><p>{t("loading")}...</p></PageLayout>;

    return (
        <PageLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 style={{ color: "#777" }}>History</h4>
                <ButtonComponent
                    className="btn"
                    style={{ backgroundColor: "#3182ce", color: "#fff" }}
                    onClick={() => openStoreModal('parent')}
                >
                    + Add History
                </ButtonComponent>
            </div>

            <div className="card-container">
                <Row className="g-0">
                    {/* LEFT COLUMN: Categories (Parent Histories) */}
                    <Col md={4} className="border-end">
                        <div className="header-strip">
                            <span>
                                <i className="material-icons align-middle me-2" style={{ fontSize: '18px' }}>folder</i>
                                Questions
                            </span>
                        </div>
                        <div style={{ height: "70vh", overflowY: "auto" }}>
                            {parentHistories.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                    No categories found
                                </div>
                            ) : (
                                parentHistories.map((parent) => (
                                    <div
                                        key={parent.id}
                                        className={`sidebar-item ${selectedParent?.id === parent.id ? 'active' : ''}`}
                                        onClick={() => setSelectedParent(parent)}
                                    >
                                        <i
                                            className="material-icons delete-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                confirmDelete(parent.id, 'parent');
                                            }}
                                        >delete</i>
                                        <span className={selectedParent?.id === parent.id ? 'text-primary' : ''}>
                                            {parent.name}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </Col>

                    {/* RIGHT COLUMN: Values (Child Histories) */}
                    <Col md={8} className="p-4">
                        <div className="d-flex justify-content-end mb-3">
                            <ButtonComponent
                                className="btn btn-sm"
                                style={{ backgroundColor: "#3182ce", color: "#fff" }}
                                onClick={() => {
                                    if (selectedParent) {
                                        openStoreModal('child', selectedParent.id);
                                    } else {
                                        Swal.fire({
                                            icon: 'warning',
                                            title: t('warning'),
                                            text: t('Please select a category first'),
                                            toast: true,
                                            position: 'top-end',
                                            showConfirmButton: false,
                                            timer: 3000,
                                            timerProgressBar: true
                                        });
                                    }
                                }}
                                disabled={!selectedParent}
                            >
                                + New Value
                            </ButtonComponent>
                        </div>

                        {!selectedParent ? (
                            <div className="text-center text-muted py-5">
                                <i className="material-icons" style={{ fontSize: '48px' }}>folder_open</i>
                                <p className="mt-2">Select a category from the left to view its values</p>
                            </div>
                        ) : childHistories.length === 0 ? (
                            <div className="text-center text-muted py-5">
                                <i className="material-icons" style={{ fontSize: '48px' }}>inbox</i>
                                <p className="mt-2">No values found for this Question</p>
                                <ButtonComponent
                                    className="btn btn-sm mt-2"
                                    style={{ backgroundColor: "#3182ce", color: "#fff" }}
                                    onClick={() => openStoreModal('child', selectedParent.id)}
                                >
                                    + Add First Value
                                </ButtonComponent>
                            </div>
                        ) : (
                            <Table responsive hover borderless>
                                <thead>
                                    <tr className="table-header">
                                        <th>Value</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {childHistories.map((child) => (
                                        <tr key={child.id} className="table-row">
                                            <td className="align-middle">
                                                <i
                                                    className="material-icons delete-icon"
                                                    onClick={() => confirmDelete(child.id, 'child')}
                                                >delete</i>
                                                {child.name}
                                            </td>
                                            <td className="text-end align-middle">
                                                <button
                                                    className="action-btn"
                                                    onClick={() => openUpdateModal(child, 'child')}
                                                >
                                                    <i className="material-icons" style={{ fontSize: "16px" }}>edit_note</i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Col>
                </Row>
            </div>

            {/* Store Modal - Add Category or Value */}
            <Modal
                show={storeModal}
                onHide={() => setStoreModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        {storeForm.parent_id ? "Add Value" : "Add History"}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleStoreSubmit}>
                    <Modal.Body className="custom-modal-body">
                        {storeForm.parent_id && selectedParent && (
                            <div className="alert alert-info mb-3">
                                <i className="material-icons me-2" style={{ fontSize: '16px' }}>info</i>
                                Adding value under: <strong>{selectedParent.name}</strong>
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={storeForm.name}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.name}
                                placeholder="Enter name"
                                autoFocus
                            />
                            <Form.Control.Feedback type="invalid">
                                {storeErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>

                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setStoreModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={storeLoading}
                        >
                            {storeLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Save
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Update Modal - Edit Category or Value */}
            <Modal
                show={updateModal}
                onHide={() => setUpdateModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Edit {currentType === 'parent' ? 'Category' : 'Value'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateSubmit}>
                    <Modal.Body className="custom-modal-body">
                        {updateErrors.general && (
                            <div className="alert alert-danger mb-3">
                                {updateErrors.general}
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={updateForm.name}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.name}
                                placeholder="Enter name"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Parent Category</Form.Label>
                            <Form.Select
                                name="parent_id"
                                className="custom-input"
                                value={updateForm.parent_id}
                                onChange={handleUpdateChange}
                            >
                                <option value="">None (Parent Category)</option>
                                {parentHistories
                                    .filter(parent => parent.id !== updateForm.id)
                                    .map(parent => (
                                        <option key={parent.id} value={parent.id}>
                                            {parent.name}
                                        </option>
                                    ))}
                            </Form.Select>
                            <small className="text-muted">
                                Select a parent to make this a child value
                            </small>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setUpdateModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={updateLoading}
                        >
                            {updateLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Update
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}